import { createSelector } from "reselect"
import { get } from "lodash"
import { getStartDate as getStartDateFromPeriod, getDatesAdjusted as getDatesAdjustedHelper, SAME_DAY } from "helpers/dates"
import { getProfileDate, getProfilePeriod } from "modules/portfolio-root/selectors/dates"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import { getToday } from "helpers/types/dates"

const getTodayDate = () => getToday()

export const getEarliestDate = state => get(state, "global.data.dates.data.model.availability.factorsStartDate")
export const getLatestDate = state => get(state, "global.data.dates.data.model.availability.currentDate")

export const getWorkingPeriod = createSelector(
  [getProfilePeriod, getLatestDate, getEarliestDate, getIsIntradayAnalyticsEnabled, getTodayDate],
  (period, latestDate, earliestDate, isIntradayAnalyticsEnabled, today) => {
    if (!isIntradayAnalyticsEnabled && period === SAME_DAY) {
      return "MTD"
    }

    const latestAvailableEndDate = isIntradayAnalyticsEnabled ? today : latestDate
    const isDateOutOfBounds = date => date && (date < earliestDate || date > latestAvailableEndDate)

    if (period && (isDateOutOfBounds(period.startDate) || isDateOutOfBounds(period.endDate))) {
      return "1Y"
    }

    return period
  },
)

export const getEndDate = createSelector(
  [getWorkingPeriod, getLatestDate, getIsIntradayAnalyticsEnabled, getTodayDate],
  (period, latestDate, isIntradayAnalyticsEnabled, today) => {
    if (period?.endDate) {
      return period.endDate
    }

    if (isIntradayAnalyticsEnabled) {
      return today
    }

    return latestDate
  },
)

export const getStartDate = createSelector(
  [getWorkingPeriod, getEndDate, getLatestDate, getEarliestDate, getIsIntradayAnalyticsEnabled],
  (period, endDate, latestDate, earliestDate, isIntradayAnalyticsEnabled) => {
    if (period?.startDate) {
      return period.startDate
    }

    const startDate = getStartDateFromPeriod(isIntradayAnalyticsEnabled ? endDate : latestDate, period)

    if (startDate < earliestDate) {
      return earliestDate
    }

    return startDate
  },
)

export const getDatesAdjusted = createSelector(
  [getWorkingPeriod, getStartDate, getEndDate, getLatestDate, getIsIntradayAnalyticsEnabled, getTodayDate],
  (period, startDate, endDate, latestDate, isIntradayAnalyticsEnabled, today) => {
    const latestAvailableEndDate = isIntradayAnalyticsEnabled ? today : latestDate
    return getDatesAdjustedHelper(period, startDate, endDate, latestAvailableEndDate)
  },
)

export const getSelectedDate = createSelector(
  [getProfileDate, getStartDate, getEndDate],
  (date, startDate, endDate) => {
    if (date && date >= startDate && date <= endDate) {
      return date
    }

    return endDate
  },
)

export const getIsEndDateToday = createSelector(
  [getEndDate, getTodayDate],
  (endDate, today) => endDate === today,
)
